import { AccountsTable } from '@/components/features/accounts/AccountsTable'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AccountStatus,
  BalanceOperator,
} from '@/domain/accounts/account'
import { useAccounts } from '@/hooks/api/useAccounts'
import { useServerPagination } from '@/hooks/useServerPagination'
import { Paths } from '@/routes/paths'
import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  Search
} from 'lucide-react'
import { useState } from 'react'
import { PageContainer } from '@/components/features/shared/PageContainer'

export const AccountsManagement = () => {
  const navigate = useNavigate()

  const searchParams = useSearch({ strict: false }) as any

  const statusQuery = searchParams.status || 'all'
  const balanceOperatorQuery = searchParams.balanceOperator || undefined
  const balanceAmountQuery = searchParams.balanceAmount ? parseFloat(searchParams.balanceAmount) : undefined

  const {
    pagination,
    setPagination: handlePaginationChange,
    search: searchQuery,
    inputValue,
    setInputValue,
    handleSearch,
    updateQueryParams,
    limit,
  } = useServerPagination({
    clearDefaults: {
      status: 'all',
      balanceOperator: '',
      balanceAmount: '',
    },
    resetOn: [statusQuery, balanceOperatorQuery, balanceAmountQuery],
  })

  const [localStatus, setLocalStatus] = useState<string>(statusQuery)
  const [balanceOperator, setBalanceOperator] = useState<BalanceOperator | undefined>(
    (balanceOperatorQuery as BalanceOperator) || undefined
  )
  const [balanceAmount, setBalanceAmount] = useState<string>(
    balanceAmountQuery?.toString() || ''
  )

  const handleApplyFilters = () => {
    updateQueryParams({
      status: localStatus,
      balanceOperator,
      balanceAmount,
    })
  }

  const { data: paginatedResponse, isLoading } = useAccounts({
    search: searchQuery || undefined,
    isMain: false,
    isCollector: false,
    status: searchParams.status === 'all' ? undefined : (searchParams.status as AccountStatus),
    balanceOperator: searchParams.balanceOperator ? (searchParams.balanceOperator as BalanceOperator) : undefined,
    balanceAmount: searchParams.balanceAmount ? parseFloat(searchParams.balanceAmount) : undefined,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
  })
  
  if (isLoading) {
    return <LoaderElement />
  }

  return (
    <PageContainer>
      <NavHeader>
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">Gestión de Cuentas</h1>
          <p className="text-muted-foreground">
            Administra todas las cuentas de los clientes
          </p>
        </div>
      </NavHeader>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex flex-col w-full max-w-sm space-y-1.5">
          <Label htmlFor="search">Buscar</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="search"
              placeholder="Cliente o número de cuenta..."
              className="pl-8"
              autoFocus
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch()
                }
              }}
            />
          </div>
        </div>
        <div className="flex flex-col space-y-1.5">
          <Label htmlFor="status">Estado</Label>
          <Select value={localStatus} onValueChange={setLocalStatus}>
            <SelectTrigger id="status" className="w-full sm:w-[180px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="active">Activas</SelectItem>
              {/* <SelectItem value="inactive">Inactivas</SelectItem> */}
              <SelectItem value="suspended">Suspendidas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="balanceOperator">Saldo</Label>
            <div className="flex gap-2">
              <Select value={balanceOperator} onValueChange={(val) => setBalanceOperator(val as BalanceOperator)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Seleccione Operador" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BalanceOperator.GREATER_THAN}>Mayor a</SelectItem>
                  <SelectItem value={BalanceOperator.LESS_THAN}>Menor a</SelectItem>
                  <SelectItem value={BalanceOperator.EQUAL_TO}>Igual a</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id="balanceAmount"
                type="number"
                placeholder="Monto"
                className="w-[140px]"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyFilters()
                  }
                }}
              />
            </div>
          </div>
        </div>
        <Button className='ml-auto self-end' onClick={handleApplyFilters} variant="info">Buscar</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Cuentas</CardTitle>
          <CardDescription>
            Todas las cuentas de los clientes del sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AccountsTable
            paginated={paginatedResponse}
            isLoading={isLoading}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            onRowClick={(account) => navigate({
              to: `${Paths.Management.Accounts}/${account.id}`
            })}
          />
        </CardContent>
      </Card>

    </PageContainer>
  )
}
